import { HttpClient } from '@angular/common/http';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { toODataString, State, FilterDescriptor } from '@progress/kendo-data-query';
import { ODataSettings } from '@progress/kendo-data-query/dist/npm/odata.operators';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Guid } from './models/guid';

export abstract class ODataBaseBinding extends BehaviorSubject<GridDataResult> {
	protected readonly baseUrl: string;

	constructor(
		protected readonly http: HttpClient,
		protected readonly serviceUrl: string,
		protected readonly resourceName: string
	) {
		super(null);
		this.baseUrl = `${serviceUrl}odata/`;
	}

	public query(state: State): void {
		this.fetch(this.resourceName, state).subscribe(
			(next) => super.next(next),
			(err) => super.error(err)
		);
	}

	protected getOdataQueryString(state: State, settings?: ODataSettings): string {
		let queryStr = '';
		let stateInsensitive: State = {};
		if (state.take) {
			// check if we have a take
			stateInsensitive.take = state.take;
		}

		if (state.skip >= 0) {
			// check if we have a skip
			stateInsensitive.skip = state.skip;
		}

		if (state.sort) {
			stateInsensitive.sort = state.sort;
		}

		if (state.filter && state.filter.filters) {
			// check if we have a filter
			stateInsensitive.filter = {
				logic: state.filter.logic,
				filters: new Array(),
			};

			for (let obj of state.filter.filters) {
				let finalObj = obj as FilterDescriptor;

				// first check for real string
				if (typeof finalObj.value === 'string') {
					// make sure not a date or guid

					if (Guid.isValid(finalObj.value) === false) {
						// worry about date later, hopefully comes in as type date
						// this checks the value side for data type, which may not always be accurate
						// we can't use convention cause KpaId was pushed upon us to use everywhere.

						if (finalObj.ignoreCase) {
							stateInsensitive.filter.filters.push({
								field: 'tolower(' + finalObj.field + ')',
								operator: finalObj.operator,
								value: finalObj.value.toLowerCase(),
							});
						} else {
							stateInsensitive.filter.filters.push({
								field: finalObj.field,
								operator: finalObj.operator,
								value: finalObj.value,
							});
						}
					} else {
						stateInsensitive.filter.filters.push(finalObj);
					}
				} else {
					// not a string so pass through
					stateInsensitive.filter.filters.push(finalObj);
				}
			}

			queryStr = toODataString(stateInsensitive, settings);
		} else {
			// no filter
			queryStr = toODataString(state, settings);
		}

		// Reformat Guid parameters
		queryStr = queryStr.replace(/'([\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12})'/gi, '$1');

		return `${queryStr}&$count=true`;
	}

	protected fetch(resourceName: string, state: State, settings?: ODataSettings): Observable<GridDataResult> {
		// Odata filters are always case sensitive unless the API uses ProjectTo.
		// To handle case sensitivity for cases where ProjectTo is not used, added the code below.
		let queryStr = this.getOdataQueryString(state, settings);

		// Remove quotes from GUID values in querystring
		return this.http.get(`${this.baseUrl}${resourceName}?${queryStr}`).pipe(
			map(
				(response) =>
					<GridDataResult>{
						data: response['value'],
						total: parseInt(response['@odata.count'], 10),
					}
			)
		);
	}
}
